/**
 * Breadcrumbs - auto-generated from the current URL path.
 *
 * Usage:
 *   import Breadcrumbs from "@/components/Breadcrumbs";
 *   <Breadcrumbs />
 *
 * Renders nothing on the homepage ("/").
 * Outputs JSON-LD BreadcrumbList schema for SEO.
 *
 * Optional overrides:
 *   <Breadcrumbs items={[{ label: "Services", href: "/services/" }, { label: "AEO Agency" }]} />
 */

import { useMemo } from "react";
import { useLocation } from "wouter";
import { ChevronRight, Home } from "lucide-react";

import SchemaScript from "@/components/SchemaScript";
export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  /** Override auto-generated items. Last item is treated as current page (no link). */
  items?: BreadcrumbItem[];
  /** Extra class on the outer <nav> */
  className?: string;
  /** Disable JSON-LD when the page's primary schema graph already defines this breadcrumb. */
  includeSchema?: boolean;
  /** Render only the BreadcrumbList when the page already has a visible trail. */
  renderVisual?: boolean;
}

// Human-readable label map for known path segments
const SEGMENT_LABELS: Record<string, string> = {
  services: "Services",
  blog: "Blog",
  resources: "Resources",
  "case-studies": "Case Studies",
  aeo: "AEO Hub",
  methodology: "Methodology",
  "modi-elnadi": "About Modi",
  contact: "Contact",
  "free-ai-growth-audit": "Free AI Audit",
  "gemini-agentic-ai": "Agentic AI Lead Gen",
  "seo-aeo-geo": "SEO, AEO & GEO",
  "ppc-performance-max": "PPC & Performance Max",
  "ai-marketing-strategy": "AI Marketing Strategy",
  "account-based-marketing": "Account-Based Marketing",
  "ai-websites": "AI Websites",
  "integrated-marketing-communications": "Integrated Marketing",
  "ai-training-for-leaders": "AI Training for Leaders",
  "google-ai-mode-optimization": "Google AI Mode Optimization",
  "preferred-sources-strategy": "Preferred Sources Strategy",
  "ai-governance": "AI Governance & Compliance",
  "aeo-agency-london": "AEO Agency London",
  "geo-agency-london": "GEO Agency London",
  "ai-marketing-agency-london": "AI Marketing Agency London",
  "ai-tools": "AI Tools Hub",
};

function toLabel(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  // Fallback: capitalise words, replace hyphens
  return segment
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function buildItems(pathname: string): BreadcrumbItem[] {
  // Strip trailing slash, split
  const parts = pathname.replace(/\/$/, "").split("/").filter(Boolean);
  if (parts.length === 0) return [];

  const items: BreadcrumbItem[] = [{ label: "Home", href: "/" }];
  let accumulated = "";
  parts.forEach((part, i) => {
    accumulated += `/${part}`;
    const isLast = i === parts.length - 1;
    items.push({
      label: toLabel(part),
      href: isLast ? undefined : `${accumulated}/`,
    });
  });
  return items;
}

function toCanonicalBreadcrumbUrl(path: string) {
  const pathname = path.split(/[?#]/, 1)[0] || "/";
  const normalizedPath = pathname === "/" ? "/" : `${pathname.replace(/\/$/, "")}/`;
  return `https://integrated.social${normalizedPath}`;
}

export default function Breadcrumbs({
  items: overrideItems,
  className = "",
  includeSchema = true,
  renderVisual = true,
}: BreadcrumbsProps) {
  const [location] = useLocation();

  const items = useMemo<BreadcrumbItem[]>(() => {
    if (overrideItems && overrideItems.length > 0) {
      // Always prepend Home if not already there
      if (overrideItems[0]?.label !== "Home") {
        return [{ label: "Home", href: "/" }, ...overrideItems];
      }
      return overrideItems;
    }
    return buildItems(location);
  }, [location, overrideItems]);

  // Don't render on homepage
  if (items.length <= 1) return null;

  // JSON-LD BreadcrumbList schema
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.label,
      "item": toCanonicalBreadcrumbUrl(item.href ?? location),
    })),
  };

  return (
    <>
      {/* JSON-LD schema */}
      {includeSchema && (
        <SchemaScript schema={JSON.stringify(schema)} />
      )}

      {renderVisual && (
        <nav
          aria-label="Breadcrumb"
          className={className}
          style={{
            padding: "10px 0",
            fontSize: "12px",
            color: "rgba(255,255,255,0.45)",
          }}
        >
          <ol
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "2px",
              listStyle: "none",
              margin: 0,
              padding: 0,
            }}
          >
            {items.map((item, i) => {
              const isFirst = i === 0;
              const isLast = i === items.length - 1;
              return (
                <li
                  key={i}
                  style={{ display: "flex", alignItems: "center", gap: "2px" }}
                  aria-current={isLast ? "page" : undefined}
                >
                  {i > 0 && (
                    <ChevronRight
                      size={12}
                      aria-hidden="true"
                      style={{ color: "rgba(255,255,255,0.2)", flexShrink: 0 }}
                    />
                  )}

                  {item.href ? (
                    <a
                      href={item.href}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "3px",
                        color: "rgba(255,255,255,0.45)",
                        textDecoration: "none",
                        transition: "color 150ms",
                        padding: "2px 4px",
                        borderRadius: "3px",
                      }}
                      onMouseEnter={e => (e.currentTarget.style.color = "#FF8C00")}
                      onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.45)")}
                    >
                      {isFirst && <Home size={11} aria-hidden="true" />}
                      <span>{item.label}</span>
                    </a>
                  ) : (
                    <span
                      style={{
                        color: "rgba(255,255,255,0.75)",
                        fontWeight: 500,
                        padding: "2px 4px",
                      }}
                    >
                      {item.label}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}
    </>
  );
}
